import type {
  FullSeafarerData,
  SeafarerProfile,
  ModularCourse,
  SeaGoingService,
  AuthorisedDocument,
  CertificateOfEndorsement,
  CertificateOfCompetency,
  Education,
  PreSeaTraining,
  PostSeaTraining,
  InternalRecordIdentity,
  ProvenanceSource,
  RecordProvenance,
  ProvenanceMap,
} from './types/seafarer';
import type { Certificate } from './db';
import { normalizeCertificateName } from '@/data/certificate-catalog';

// ── Re-export types for consumers ─────────────────────────────────────────────

export type { ProvenanceSource, RecordProvenance, ProvenanceMap };

/**
 * Which kind of PDF an import came from. DG profiles are authoritative; maritime
 * CVs are lower fidelity and may phrase the same records differently, so a CV
 * import must enrich and de-duplicate against DG data rather than replace it.
 */
export type ImportSourceType = 'dg-profile' | 'maritime-cv';

/**
 * Relative trust of a record's origin, used to pick the record that survives
 * when two sources describe the same real-world certificate/service. Higher
 * wins. User intent outranks any import; DG outranks CV.
 */
function sourceAuthorityScore(source?: ProvenanceSource): number {
  switch (source) {
    case 'user_corrected': return 1000;
    case 'manual': return 800;
    case 'dg_import': return 400;
    case 'cv_import': return 100;
    default: return 0;
  }
}

// ── Stable identity key helpers ───────────────────────────────────────────────

function norm(s?: string | null): string {
  return (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Normalize a certificate/document number for identity matching.
 *  Strips spaces, hyphens, slashes so "MUM/01/12345" == "MUM 01 12345" == "MUM0112345". */
function normCertNumber(s?: string | null): string {
  return (s ?? '').trim().toUpperCase().replace(/[\s./-]/g, '');
}

export function courseKey(c: ModularCourse): string {
  // Include validUpto so a renewed cert (same courseNo/name/date but new expiry) keeps a distinct key.
  return `course:${normCertNumber(c.certificateNo)}|${norm(c.courseName)}|${norm(c.trainingInstitute)}|${norm(c.dateOfIssue)}|${norm(c.validUpto)}`;
}

export function serviceKey(s: SeaGoingService): string {
  // Treat missing rank as a distinct value to avoid conflating two roles on the same ship.
  const rank = (s.rank ?? '').trim() || 'unknown-rank';
  return `svc:${norm(s.imoNumber)}|${norm(s.shipName)}|${norm(s.serviceFrom)}|${norm(s.serviceTo)}|${norm(rank)}`;
}

export function docKey(d: AuthorisedDocument): string {
  return `doc:${normCertNumber(d.documentNumber)}|${norm(d.documentType)}|${norm(d.issuedAt)}|${norm(d.issuedDate)}`;
}

export function coeKey(e: CertificateOfEndorsement): string {
  return `coe:${norm(e.certificateNo)}|${norm(e.endorsementType)}|${norm(e.issuedAt)}|${norm(e.dateOfIssue)}`;
}

function meaningfulIdentifier(value?: string | null): string {
  const id = norm(value);
  return id && !['n/a', 'na', 'nil', 'none', 'unknown', '-'].includes(id) ? id : '';
}

function seaServiceScore(
  service: SeaGoingService,
  provenanceMap?: ProvenanceMap,
): number {
  const provenance = service._recordId ? provenanceMap?.[service._recordId] : undefined;
  const sourceScore = sourceAuthorityScore(provenance?.source);
  const detailScore = [
    service.companyName,
    service.imoNumber,
    service.officialNo,
    service.flag,
    service.portOfRegistry,
    service.shipType,
    service.grossTonnage,
    service.tradeArea,
    service.rank,
    service.propulsionPowerKw,
    service.propulsionType,
    service.natureOfWatchkeeping,
  ].filter(value => norm(value)).length * 10;
  const durationScore = [service.articleMonths, service.articleDays, service.propellingDays]
    .filter(value => value !== null && value !== undefined).length * 5;
  return sourceScore + detailScore + durationScore;
}

function parseServiceDate(value?: string | null): number | null {
  const match = (value ?? '').trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() + 1 !== Number(month)
    || date.getUTCDate() !== Number(day)
  ) return null;
  return date.getTime();
}

function vesselNameIdentity(value?: string | null): string {
  return norm(value)
    .replace(/[.,]/g, '')
    .replace(/\//g, '')
    .replace(/^(?:m\s*v|m\s*t|m\s*y|mv|mt|my|ms|ss|lpgc|lngc)\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function distinctiveVesselSuffix(value: string): string {
  const tokens = value.split(' ').filter(Boolean);
  const last = tokens.at(-1) ?? '';
  return last.length >= 5 ? last : '';
}

function sameVessel(left: SeaGoingService, right: SeaGoingService): boolean {
  const leftImo = meaningfulIdentifier(left.imoNumber);
  const rightImo = meaningfulIdentifier(right.imoNumber);
  if (leftImo && rightImo) return leftImo === rightImo;

  const leftShip = vesselNameIdentity(left.shipName);
  const rightShip = vesselNameIdentity(right.shipName);
  if (!leftShip || !rightShip) return false;
  if (leftShip === rightShip || leftShip.includes(rightShip) || rightShip.includes(leftShip)) return true;

  const leftSuffix = distinctiveVesselSuffix(leftShip);
  const rightSuffix = distinctiveVesselSuffix(rightShip);
  return !!leftSuffix && leftSuffix === rightSuffix;
}

function sameVoyagePeriod(left: SeaGoingService, right: SeaGoingService): boolean {
  const leftFrom = parseServiceDate(left.serviceFrom);
  const leftTo = parseServiceDate(left.serviceTo);
  const rightFrom = parseServiceDate(right.serviceFrom);
  const rightTo = parseServiceDate(right.serviceTo);
  if (leftFrom === null || leftTo === null || rightFrom === null || rightTo === null) {
    const leftFromRaw = meaningfulIdentifier(left.serviceFrom);
    const leftToRaw = meaningfulIdentifier(left.serviceTo);
    const rightFromRaw = meaningfulIdentifier(right.serviceFrom);
    const rightToRaw = meaningfulIdentifier(right.serviceTo);
    return !!leftFromRaw && !!leftToRaw
      && leftFromRaw === rightFromRaw
      && leftToRaw === rightToRaw;
  }

  const day = 24 * 60 * 60 * 1000;
  const boundaryTolerance = 45 * day;
  const sameStart = Math.abs(leftFrom - rightFrom) <= boundaryTolerance;
  const sameEnd = Math.abs(leftTo - rightTo) <= boundaryTolerance;
  const overlap = Math.max(0, Math.min(leftTo, rightTo) - Math.max(leftFrom, rightFrom));
  const shorterDuration = Math.max(day, Math.min(leftTo - leftFrom, rightTo - rightFrom));
  return (overlap > 0 && sameStart && sameEnd) || overlap / shorterDuration >= 0.8;
}

function sameVoyage(left: SeaGoingService, right: SeaGoingService): boolean {
  return sameVessel(left, right) && sameVoyagePeriod(left, right);
}

function mergeSeaServiceGroup(
  services: SeaGoingService[],
  indices: number[],
  preferredIdx: number,
): SeaGoingService {
  const preferred = services[preferredIdx];
  const merged = { ...preferred };
  const keys = Array.from(new Set(
    indices.flatMap(idx => Object.keys(services[idx])),
  )) as Array<keyof SeaGoingService>;

  for (const idx of indices) {
    const candidate = services[idx];
    for (const key of keys) {
      if (key === '_recordId') continue;
      const current = merged[key];
      const incoming = candidate[key];
      if (
        (current === '' || current === null || current === undefined)
        && incoming !== '' && incoming !== null && incoming !== undefined
      ) {
        (merged as Record<string, unknown>)[key] = incoming;
      }
    }
  }
  return merged;
}

/**
 * One substantially matching vessel assignment is one sea-service record.
 * Complementary fields from duplicate source rows are merged into the preferred
 * record; genuinely separate date ranges remain separate assignments.
 */
export function deduplicateSeaServiceEntries(
  services: SeaGoingService[],
  provenanceMap?: ProvenanceMap,
): Array<{ service: SeaGoingService; idx: number }> {
  const groups: number[][] = [];

  services.forEach((service, idx) => {
    const matchingGroup = groups.find(indices =>
      indices.some(existingIdx => sameVoyage(services[existingIdx], service)),
    );
    if (matchingGroup) matchingGroup.push(idx);
    else groups.push([idx]);
  });

  return groups.map(indices => {
    const idx = indices.reduce((best, candidate) =>
      seaServiceScore(services[candidate], provenanceMap) > seaServiceScore(services[best], provenanceMap)
        ? candidate
        : best,
    );
    return { service: mergeSeaServiceGroup(services, indices, idx), idx };
  }).sort((a, b) => a.idx - b.idx);
}

function consolidateSeaServices(
  services: SeaGoingService[],
  provenanceMap: ProvenanceMap,
): { services: SeaGoingService[]; provenanceMap: ProvenanceMap } {
  const entries = deduplicateSeaServiceEntries(services, provenanceMap);
  const keptIds = new Set(entries.map(entry => entry.service._recordId).filter(Boolean));
  const outMap = Object.fromEntries(
    Object.entries(provenanceMap).filter(([id]) =>
      !services.some(service => service._recordId === id) || keptIds.has(id),
    ),
  );
  return {
    services: entries.map(entry => entry.service),
    provenanceMap: outMap,
  };
}

function endorsementScore(
  endorsement: CertificateOfEndorsement,
  provenanceMap?: ProvenanceMap,
): number {
  const provenance = endorsement._recordId ? provenanceMap?.[endorsement._recordId] : undefined;
  const sourceScore = sourceAuthorityScore(provenance?.source);
  const detailScore = [
    endorsement.tankerType,
    endorsement.level,
    endorsement.issuedAt,
    endorsement.dateOfIssue,
    endorsement.validUpto,
  ].filter(value => norm(value)).length * 10;
  const specificTypeScore = /oil|chemical|gas|lpg|lng/i.test(endorsement.endorsementType ?? '') ? 50 : 0;
  return sourceScore + detailScore + specificTypeScore;
}

function preferredEndorsementIndex(
  endorsements: CertificateOfEndorsement[],
  indices: number[],
  provenanceMap?: ProvenanceMap,
): number {
  return indices.reduce((best, candidate) =>
    endorsementScore(endorsements[candidate], provenanceMap) > endorsementScore(endorsements[best], provenanceMap)
      ? candidate
      : best,
  );
}

/** One issued certificate number represents one real endorsement record. */
export function deduplicateEndorsementEntries(
  endorsements: CertificateOfEndorsement[],
  provenanceMap?: ProvenanceMap,
): Array<{ endorsement: CertificateOfEndorsement; idx: number }> {
  const groups = new Map<string, number[]>();
  const unnumbered: number[] = [];

  endorsements.forEach((endorsement, idx) => {
    const number = meaningfulIdentifier(endorsement.certificateNo);
    if (!number) {
      unnumbered.push(idx);
      return;
    }
    const issueDate = meaningfulIdentifier(endorsement.dateOfIssue);
    const key = `${number}|${issueDate}`;
    groups.set(key, [...(groups.get(key) ?? []), idx]);
  });

  return [
    ...Array.from(groups.values()).map(indices => {
      const idx = preferredEndorsementIndex(endorsements, indices, provenanceMap);
      return { endorsement: endorsements[idx], idx };
    }),
    ...unnumbered.map(idx => ({ endorsement: endorsements[idx], idx })),
  ].sort((a, b) => a.idx - b.idx);
}

function consolidateEndorsements(
  endorsements: CertificateOfEndorsement[],
  provenanceMap: ProvenanceMap,
): { endorsements: CertificateOfEndorsement[]; provenanceMap: ProvenanceMap } {
  const entries = deduplicateEndorsementEntries(endorsements, provenanceMap);
  const keptIds = new Set(entries.map(entry => entry.endorsement._recordId).filter(Boolean));
  const outMap = Object.fromEntries(
    Object.entries(provenanceMap).filter(([id]) =>
      !endorsements.some(endorsement => endorsement._recordId === id) || keptIds.has(id),
    ),
  );
  return {
    endorsements: entries.map(entry => entry.endorsement),
    provenanceMap: outMap,
  };
}

function competencyScore(
  coc: CertificateOfCompetency,
  provenanceMap?: ProvenanceMap,
): number {
  const provenance = coc._recordId ? provenanceMap?.[coc._recordId] : undefined;
  const sourceScore = sourceAuthorityScore(provenance?.source);
  const detailScore = [
    coc.issuedAt,
    coc.placeOfExamination,
    coc.certificateDelivered,
    coc.certificateType,
    coc.issuingCountry,
    coc.issueDate,
    coc.validUpto,
  ].filter(value => norm(value)).length * 10;
  const descriptiveGradeScore = (coc.grade ?? '').trim().split(/\s+/).length * 5;
  return sourceScore + detailScore + descriptiveGradeScore;
}

/**
 * One issued CoC number represents one real competency certificate.
 *
 * The certificate number is the document's identity, so all rows sharing a
 * number collapse to a single CoC regardless of issue-date variance (the DG
 * profile and a CV often restate the same CoC with a renewal/print date that
 * differs by a few days). Rows without a meaningful number cannot be linked and
 * are each kept as-is.
 */
export function deduplicateCompetencyEntries(
  competencies: CertificateOfCompetency[],
  provenanceMap?: ProvenanceMap,
): Array<{ coc: CertificateOfCompetency; idx: number }> {
  const groups = new Map<string, number[]>();
  const ambiguous: number[] = [];

  competencies.forEach((coc, idx) => {
    const number = meaningfulIdentifier(coc.certificateNumber);
    if (!number) {
      ambiguous.push(idx);
      return;
    }
    groups.set(number, [...(groups.get(number) ?? []), idx]);
  });

  return [
    ...Array.from(groups.values()).map(indices => {
      const idx = indices.reduce((best, candidate) =>
        competencyScore(competencies[candidate], provenanceMap) > competencyScore(competencies[best], provenanceMap)
          ? candidate
          : best,
      );
      return { coc: competencies[idx], idx };
    }),
    ...ambiguous.map(idx => ({ coc: competencies[idx], idx })),
  ].sort((a, b) => a.idx - b.idx);
}

function consolidateCompetencies(
  competencies: CertificateOfCompetency[],
  provenanceMap: ProvenanceMap,
): { competencies: CertificateOfCompetency[]; provenanceMap: ProvenanceMap } {
  const entries = deduplicateCompetencyEntries(competencies, provenanceMap);
  const keptIds = new Set(entries.map(entry => entry.coc._recordId).filter(Boolean));
  const outMap = Object.fromEntries(
    Object.entries(provenanceMap).filter(([id]) =>
      !competencies.some(coc => coc._recordId === id) || keptIds.has(id),
    ),
  );
  return {
    competencies: entries.map(entry => entry.coc),
    provenanceMap: outMap,
  };
}

// ── Cross-source modular course / document consolidation ──────────────────────
//
// DG profiles and maritime CVs describe the same certificate with different
// wording, so exact content keys cannot link them. These helpers collapse the
// same real-world course/document across sources using number-first identity
// with a canonical-family + date-proximity fallback, then keep the
// highest-authority record and gap-fill its blanks from the duplicates.

const COURSE_DATE_TOLERANCE_MS = 90 * 24 * 60 * 60 * 1000;

/** Gap-fills the preferred record's empty fields from its duplicate group. */
function mergeRecordGroup<T extends InternalRecordIdentity>(
  items: T[],
  indices: number[],
  preferredIdx: number,
): T {
  const merged: Record<string, unknown> = { ...(items[preferredIdx] as object) };
  for (const idx of indices) {
    const candidate = items[idx] as Record<string, unknown>;
    for (const key of Object.keys(candidate)) {
      if (key === '_recordId') continue;
      const current = merged[key];
      const incoming = candidate[key];
      if (
        (current === '' || current === null || current === undefined)
        && incoming !== '' && incoming !== null && incoming !== undefined
      ) {
        merged[key] = incoming;
      }
    }
  }
  return merged as T;
}

/**
 * Groups records by a same-record predicate, keeping the best-scored per group.
 *
 * Only records from DIFFERENT sources are ever fuzzy-merged. Two rows from the
 * same import (both DG, or both CV) are trusted as the parser emitted them and
 * stay distinct — this preserves genuine duplicate rows and same-number/
 * different-type records, while still collapsing a DG record and its CV
 * restatement of the same real-world certificate.
 */
function deduplicateByMatch<T extends InternalRecordIdentity>(
  items: T[],
  sameRecord: (a: T, b: T) => boolean,
  scoreOf: (item: T) => number,
  sourceOf: (item: T) => ProvenanceSource | undefined,
): Array<{ item: T; idx: number }> {
  const groups: number[][] = [];
  items.forEach((item, idx) => {
    const group = groups.find(indices =>
      indices.some(i => sourceOf(items[i]) !== sourceOf(item) && sameRecord(items[i], item)),
    );
    if (group) group.push(idx);
    else groups.push([idx]);
  });
  return groups
    .map(indices => {
      const best = indices.reduce((b, c) => (scoreOf(items[c]) > scoreOf(items[b]) ? c : b));
      return { item: mergeRecordGroup(items, indices, best), idx: best };
    })
    .sort((a, b) => a.idx - b.idx);
}

function courseFamily(name?: string): string {
  const text = (name ?? '').trim();
  if (!text) return '';
  return normalizeCertificateName(text)?.canonicalId ?? norm(text);
}

function courseIssueMs(course: ModularCourse): number | null {
  return parseServiceDate(course.dateOfIssue) ?? parseServiceDate(course.attendedTo);
}

function sameCourse(a: ModularCourse, b: ModularCourse): boolean {
  const an = meaningfulIdentifier(a.certificateNo);
  const bn = meaningfulIdentifier(b.certificateNo);
  if (an && bn) return an === bn;            // distinct numbers ⇒ distinct certs (renewals stay separate)
  const af = courseFamily(a.courseName);
  const bf = courseFamily(b.courseName);
  if (!af || !bf || af !== bf) return false; // different course type ⇒ different record
  const ad = courseIssueMs(a);
  const bd = courseIssueMs(b);
  if (ad !== null && bd !== null) return Math.abs(ad - bd) <= COURSE_DATE_TOLERANCE_MS;
  return true;                               // same family, a CV omitted the date ⇒ treat as same course
}

function documentFamily(type?: string): string {
  const text = norm(type);
  if (/passport/.test(text)) return 'passport';
  if (/cdc|continuous discharge/.test(text)) return 'cdc';
  if (/indos/.test(text)) return 'indos';
  if (/exn/.test(text)) return 'exn';
  return text;
}

function sameDocument(a: AuthorisedDocument, b: AuthorisedDocument): boolean {
  const af = documentFamily(a.documentType);
  const bf = documentFamily(b.documentType);
  if (!af || !bf || af !== bf) return false; // different type ⇒ different document (number namespaces differ)
  const an = meaningfulIdentifier(a.documentNumber);
  const bn = meaningfulIdentifier(b.documentNumber);
  if (an && bn) return an === bn;            // same type, distinct numbers ⇒ e.g. renewed passport
  return true;                               // same singular type, a source omitted the number
}

function nonEmptyCount(values: Array<unknown>): number {
  return values.filter(v => v !== '' && v !== null && v !== undefined && norm(String(v)) !== '').length;
}

function consolidateModularCourses(
  courses: ModularCourse[],
  provenanceMap: ProvenanceMap,
): { courses: ModularCourse[]; provenanceMap: ProvenanceMap } {
  const scoreOf = (course: ModularCourse): number => {
    const provenance = course._recordId ? provenanceMap[course._recordId] : undefined;
    const detail = nonEmptyCount([
      course.stcwCode, course.trainingInstitute, course.attendedFrom,
      course.attendedTo, course.certificateNo, course.dateOfIssue, course.validUpto,
    ]) * 10;
    return sourceAuthorityScore(provenance?.source) + detail;
  };
  const sourceOf = (course: ModularCourse) =>
    course._recordId ? provenanceMap[course._recordId]?.source : undefined;
  const entries = deduplicateByMatch(courses, sameCourse, scoreOf, sourceOf);
  const keptIds = new Set(entries.map(entry => entry.item._recordId).filter(Boolean));
  const outMap = Object.fromEntries(
    Object.entries(provenanceMap).filter(([id]) =>
      !courses.some(course => course._recordId === id) || keptIds.has(id),
    ),
  );
  return { courses: entries.map(entry => entry.item), provenanceMap: outMap };
}

function consolidateAuthorisedDocuments(
  documents: AuthorisedDocument[],
  provenanceMap: ProvenanceMap,
): { documents: AuthorisedDocument[]; provenanceMap: ProvenanceMap } {
  const scoreOf = (document: AuthorisedDocument): number => {
    const provenance = document._recordId ? provenanceMap[document._recordId] : undefined;
    const detail = nonEmptyCount([
      document.documentNumber, document.issuedDate, document.issuedAt, document.validUpto,
    ]) * 10;
    return sourceAuthorityScore(provenance?.source) + detail;
  };
  const sourceOf = (document: AuthorisedDocument) =>
    document._recordId ? provenanceMap[document._recordId]?.source : undefined;
  const entries = deduplicateByMatch(documents, sameDocument, scoreOf, sourceOf);
  const keptIds = new Set(entries.map(entry => entry.item._recordId).filter(Boolean));
  const outMap = Object.fromEntries(
    Object.entries(provenanceMap).filter(([id]) =>
      !documents.some(document => document._recordId === id) || keptIds.has(id),
    ),
  );
  return { documents: entries.map(entry => entry.item), provenanceMap: outMap };
}

export function cocKey(c: CertificateOfCompetency): string {
  return `coc:${norm(c.certificateNumber)}|${norm(c.grade)}|${norm(c.issuingCountry)}|${norm(c.issueDate)}`;
}

export function educationKey(e: Education): string {
  return `edu:${norm(e.instituteName)}|${norm(e.yearOfPassing)}|${norm(e.certificateNo)}`;
}

export function preSeaKey(p: PreSeaTraining): string {
  return `pre:${norm(p.certificateNo)}|${norm(p.courseName)}|${norm(p.trainingInstitute)}|${norm(p.attendedFrom)}`;
}

export function postSeaKey(p: PostSeaTraining): string {
  return `post:${norm(p.certificateNo)}|${norm(p.courseName)}|${norm(p.trainingInstitute)}|${norm(p.attendedFrom)}`;
}

type ArraySection = keyof Omit<FullSeafarerData, 'profile' | '_provenanceMap'>;

const KEY_FNS: Record<ArraySection, (item: any) => string> = {
  modularCourses: courseKey,
  seaGoingService: serviceKey,
  authorisedDocuments: docKey,
  certificatesOfEndorsement: coeKey,
  certificatesOfCompetency: cocKey,
  education: educationKey,
  preSeaTraining: preSeaKey,
  postSeaTraining: postSeaKey,
};

export function getSectionKeyFn(section: ArraySection): (item: any) => string {
  return KEY_FNS[section];
}

// ── Profile personal-field merge ──────────────────────────────────────────────

/**
 * Merges personal SeafarerProfile fields.
 * Existing non-empty values are preserved; empty slots are filled from incoming.
 * parsedAt always uses the incoming (newest) timestamp.
 * profilePhoto is always taken from the existing record if present.
 * age is always recalculated from dateOfBirth so it never goes stale.
 */
function mergeProfileFields(
  existing: SeafarerProfile,
  incoming: SeafarerProfile,
): SeafarerProfile {
  const keepStr = (e: string, i: string): string => ((e ?? '').trim() !== '' ? e : i);
  const keepNullable = <T>(e: T | null, i: T | null): T | null => (e !== null ? e : i);

  const dob = keepStr(existing.dateOfBirth, incoming.dateOfBirth);
  let age: number | null = keepNullable(existing.age, incoming.age);
  if (dob) {
    const parts = dob.trim().split('/');
    if (parts.length === 3) {
      const [d, m, y] = parts.map(Number);
      if (d && m && y) {
        const dobMs = Date.UTC(y, m - 1, d);
        const computed = Math.floor((Date.now() - dobMs) / (365.25 * 24 * 60 * 60 * 1000));
        if (computed >= 16 && computed <= 80) age = computed;
      }
    }
  }

  return {
    fullName:         keepStr(existing.fullName, incoming.fullName),
    givenName:        keepStr(existing.givenName, incoming.givenName),
    surname:          keepStr(existing.surname, incoming.surname),
    indosNumber:      keepStr(existing.indosNumber, incoming.indosNumber),
    discipline:       keepStr(existing.discipline, incoming.discipline),
    gender:           keepStr(existing.gender, incoming.gender),
    dateOfBirth:      dob,
    age,
    placeOfBirth:     keepStr(existing.placeOfBirth, incoming.placeOfBirth),
    birthState:       keepStr(existing.birthState, incoming.birthState),
    birthCountry:     keepStr(existing.birthCountry, incoming.birthCountry),
    nationality:      keepStr(existing.nationality, incoming.nationality),
    presentAddress:   keepStr(existing.presentAddress, incoming.presentAddress),
    permanentAddress: keepStr(existing.permanentAddress, incoming.permanentAddress),
    mobileNumber:     keepStr(existing.mobileNumber, incoming.mobileNumber),
    telephoneNumber:  keepStr(existing.telephoneNumber, incoming.telephoneNumber),
    email:            keepStr(existing.email, incoming.email),
    physical:         keepNullable(existing.physical, incoming.physical),
    currentRank:      keepStr(existing.currentRank, incoming.currentRank),
    profilePhoto:     existing.profilePhoto ?? incoming.profilePhoto,
    parsedAt:         incoming.parsedAt,
  };
}

// ── Immutable row identity helpers ────────────────────────────────────────────

function uniqueRecordId(prefix: string, used: Set<string>): string {
  let id: string;
  do {
    id = `${prefix}:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
  } while (used.has(id));
  used.add(id);
  return id;
}

function importedRecordId(contentKey: string, used: Set<string>): string {
  if (!used.has(contentKey)) {
    used.add(contentKey);
    return contentKey;
  }
  let occurrence = 2;
  while (used.has(`${contentKey}#${occurrence}`)) occurrence += 1;
  const id = `${contentKey}#${occurrence}`;
  used.add(id);
  return id;
}

function withRecordId<T>(item: T, recordId: string): T {
  return { ...(item as object), _recordId: recordId } as T;
}

/** Returns provenance for a concrete stored row without guessing from duplicates. */
export function getRecordProvenance<T extends InternalRecordIdentity>(
  data: FullSeafarerData,
  item: T,
  keyFn: (item: T) => string,
): RecordProvenance | undefined {
  if (item._recordId && data._provenanceMap?.[item._recordId]) {
    return data._provenanceMap[item._recordId];
  }
  const contentKey = keyFn(item);
  const matches = Object.values(data._provenanceMap ?? {}).filter(
    p => (p.contentKey ?? p.recordId) === contentKey,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

// ── Section-level merge ───────────────────────────────────────────────────────

function mergeSection<T extends InternalRecordIdentity>(
  existing: T[],
  incoming: T[],
  keyFn: (item: T) => string,
  provenanceMap: ProvenanceMap,
  now: string,
  sourceType: ImportSourceType = 'dg-profile',
): { merged: T[]; provenanceMap: ProvenanceMap } {
  const outMap: ProvenanceMap = { ...provenanceMap };
  const result: T[] = [];
  const usedIds = new Set(Object.keys(outMap));
  const claimedLegacyIds = new Set<string>();
  const incomingByKey = new Map<string, T[]>();
  // A maritime-CV import is a lower-authority, partial source. It must never
  // demote DG records to historical just because the CV phrased or omitted them
  // differently; cross-source de-duplication happens later in consolidation.
  const isCvImport = sourceType === 'maritime-cv';
  const incomingSource: ProvenanceSource = isCvImport ? 'cv_import' : 'dg_import';

  for (const item of incoming) {
    const key = keyFn(item);
    incomingByKey.set(key, [...(incomingByKey.get(key) ?? []), item]);
  }

  const takeIncoming = (...keys: Array<string | undefined>): T | undefined => {
    for (const key of keys) {
      if (!key) continue;
      const queue = incomingByKey.get(key);
      if (queue?.length) return queue.shift();
    }
    return undefined;
  };

  const findLegacyStableId = (contentKey: string): string | undefined => {
    const candidates = Object.entries(outMap).filter(([id, prov]) =>
      !claimedLegacyIds.has(id) && (prov.contentKey ?? id) === contentKey,
    );
    if (candidates.length !== 1) return undefined;
    claimedLegacyIds.add(candidates[0][0]);
    return candidates[0][0];
  };

  for (const originalItem of existing) {
    const contentKey = keyFn(originalItem);
    const stableId = originalItem._recordId
      ?? findLegacyStableId(contentKey)
      ?? uniqueRecordId('legacy', usedIds);
    usedIds.add(stableId);
    const item = withRecordId(originalItem, stableId);
    const existingProv = outMap[stableId];
    const prov: RecordProvenance = existingProv ?? {
      source: 'dg_import',
      recordId: stableId,
      contentKey,
      importKey: contentKey,
      isLegacy: true,
    };

    const match = takeIncoming(prov.importKey, prov.contentKey, contentKey);

    if (prov.source === 'manual' || prov.source === 'user_corrected' || prov.isLegacy) {
      outMap[stableId] = {
        ...prov,
        recordId: stableId,
        contentKey,
        // Only a same-authority DG re-import may demote a missing legacy DG row.
        isHistorical: prov.source === 'dg_import' && !isCvImport ? !match : prov.isHistorical,
        ...(match && { importedAt: now }),
      };
      result.push(item);
      continue;
    }

    if (match) {
      const fresh = withRecordId(match, stableId);
      // Keep the higher authority: a CV that happens to match a DG row stays DG.
      const matchedSource: ProvenanceSource =
        sourceAuthorityScore(prov.source) >= sourceAuthorityScore(incomingSource)
          ? prov.source
          : incomingSource;
      outMap[stableId] = {
        source: matchedSource,
        recordId: stableId,
        contentKey: keyFn(match),
        importKey: prov.importKey ?? contentKey,
        importedAt: now,
        isHistorical: false,
      };
      result.push(fresh);
    } else {
      // No match. A DG re-import demotes the missing DG row to history; a CV
      // import leaves it active and lets consolidation reconcile any duplicate.
      outMap[stableId] = {
        ...prov,
        recordId: stableId,
        contentKey,
        isHistorical: isCvImport ? prov.isHistorical : true,
      };
      result.push(item);
    }
  }

  for (const [incomingKey, queue] of incomingByKey) {
    for (const item of queue) {
      const stableId = importedRecordId(incomingKey, usedIds);
      outMap[stableId] = {
        source: incomingSource,
        recordId: stableId,
        contentKey: incomingKey,
        importKey: incomingKey,
        importedAt: now,
      };
      result.push(withRecordId(item, stableId));
    }
  }

  return { merged: result, provenanceMap: outMap };
}

// ── Current rank derivation ───────────────────────────────────────────────────

/**
 * Derives currentRank from the most recent active (non-historical) sea service
 * entry that carries a rank. Falls back to the profile value if nothing found.
 */
function deriveCurrentRank(
  services: SeaGoingService[],
  provenanceMap: ProvenanceMap,
  profileRank: string,
): string {
  const active = services.filter(s => !s._recordId || !provenanceMap[s._recordId]?.isHistorical);
  // Sort descending by serviceTo (most-recent voyage last in typical list, so we reverse and find)
  const sorted = [...active].sort((a, b) => {
    const ta = parseServiceDate(a.serviceTo) ?? parseServiceDate(a.serviceFrom) ?? 0;
    const tb = parseServiceDate(b.serviceTo) ?? parseServiceDate(b.serviceFrom) ?? 0;
    return tb - ta;
  });
  const latest = sorted.find(s => (s.rank ?? '').trim());
  return latest?.rank?.trim() || profileRank;
}

// ── Profile import merge ──────────────────────────────────────────────────────

/**
 * Merges a freshly imported FullSeafarerData into the existing stored data.
 *
 * Merge rules per array section:
 * - New DG records: added as active dg_import.
 * - Matching explicit dg_import records: refreshed from the newest import.
 * - manual / user_corrected records: always preserved; no DG duplicate added.
 * - DG records absent from the new PDF: retained and marked isHistorical=true.
 * - Legacy records (no provenance entry): stored values kept as-is permanently
 *   (isLegacy=true), since we cannot distinguish DG data from user corrections.
 * - User-corrected records whose identity key changed (importKey set): the
 *   incoming DG key is recognised via importKey reverse-index and not duplicated.
 *
 * Profile personal fields: existing non-empty values are preserved; empty slots
 * are filled from the incoming DG data. parsedAt always uses incoming.
 * profilePhoto is always preserved from the existing stored profile.
 */
export function mergeProfileImport(
  existing: FullSeafarerData | null,
  incoming: FullSeafarerData,
  now = new Date().toISOString(),
  sourceType: ImportSourceType = 'dg-profile',
): FullSeafarerData {
  if (!existing) {
    let pMap: ProvenanceMap = {};
    const firstImport = <T extends InternalRecordIdentity>(
      items: T[] | undefined,
      keyFn: (i: T) => string,
    ): T[] => {
      const merged = mergeSection([], items ?? [], keyFn, pMap, now, sourceType);
      pMap = merged.provenanceMap;
      return merged.merged;
    };
    const endorsements = firstImport(incoming.certificatesOfEndorsement, coeKey);
    const consolidated = consolidateEndorsements(endorsements, pMap);
    pMap = consolidated.provenanceMap;
    const competencies = firstImport(incoming.certificatesOfCompetency, cocKey);
    const consolidatedCompetencies = consolidateCompetencies(competencies, pMap);
    pMap = consolidatedCompetencies.provenanceMap;
    const services = firstImport(incoming.seaGoingService, serviceKey);
    const consolidatedServices = consolidateSeaServices(services, pMap);
    pMap = consolidatedServices.provenanceMap;
    const courses = firstImport(incoming.modularCourses, courseKey);
    const consolidatedCourses = consolidateModularCourses(courses, pMap);
    pMap = consolidatedCourses.provenanceMap;
    const documents = firstImport(incoming.authorisedDocuments, docKey);
    const consolidatedDocuments = consolidateAuthorisedDocuments(documents, pMap);
    pMap = consolidatedDocuments.provenanceMap;
    const derivedRank = deriveCurrentRank(consolidatedServices.services, pMap, incoming.profile?.currentRank ?? '');
    return {
      profile: { ...incoming.profile, currentRank: derivedRank },
      education: firstImport(incoming.education, educationKey),
      preSeaTraining: firstImport(incoming.preSeaTraining, preSeaKey),
      modularCourses: consolidatedCourses.courses,
      seaGoingService: consolidatedServices.services,
      postSeaTraining: firstImport(incoming.postSeaTraining, postSeaKey),
      certificatesOfEndorsement: consolidated.endorsements,
      certificatesOfCompetency: consolidatedCompetencies.competencies,
      authorisedDocuments: consolidatedDocuments.documents,
      _provenanceMap: pMap,
    };
  }

  let pMap: ProvenanceMap = { ...(existing._provenanceMap ?? {}) };

  const doMerge = <T extends InternalRecordIdentity>(
    existingItems: T[] | undefined,
    incomingItems: T[] | undefined,
    keyFn: (i: T) => string,
  ): T[] => {
    const { merged, provenanceMap } = mergeSection(
      existingItems ?? [],
      incomingItems ?? [],
      keyFn,
      pMap,
      now,
      sourceType,
    );
    pMap = provenanceMap;
    return merged;
  };

  const endorsements = doMerge(existing.certificatesOfEndorsement, incoming.certificatesOfEndorsement, coeKey);
  const consolidated = consolidateEndorsements(endorsements, pMap);
  pMap = consolidated.provenanceMap;
  const competencies = doMerge(existing.certificatesOfCompetency, incoming.certificatesOfCompetency, cocKey);
  const consolidatedCompetencies = consolidateCompetencies(competencies, pMap);
  pMap = consolidatedCompetencies.provenanceMap;
  const services = doMerge(existing.seaGoingService, incoming.seaGoingService, serviceKey);
  const consolidatedServices = consolidateSeaServices(services, pMap);
  pMap = consolidatedServices.provenanceMap;
  const courses = doMerge(existing.modularCourses, incoming.modularCourses, courseKey);
  const consolidatedCourses = consolidateModularCourses(courses, pMap);
  pMap = consolidatedCourses.provenanceMap;
  const documents = doMerge(existing.authorisedDocuments, incoming.authorisedDocuments, docKey);
  const consolidatedDocuments = consolidateAuthorisedDocuments(documents, pMap);
  pMap = consolidatedDocuments.provenanceMap;
  const mergedProfile = mergeProfileFields(existing.profile, incoming.profile);
  const derivedRank = deriveCurrentRank(consolidatedServices.services, pMap, mergedProfile.currentRank ?? '');
  return {
    profile: { ...mergedProfile, currentRank: derivedRank },
    education: doMerge(existing.education, incoming.education, educationKey),
    preSeaTraining: doMerge(existing.preSeaTraining, incoming.preSeaTraining, preSeaKey),
    modularCourses: consolidatedCourses.courses,
    seaGoingService: consolidatedServices.services,
    postSeaTraining: doMerge(existing.postSeaTraining, incoming.postSeaTraining, postSeaKey),
    certificatesOfEndorsement: consolidated.endorsements,
    certificatesOfCompetency: consolidatedCompetencies.competencies,
    authorisedDocuments: consolidatedDocuments.documents,
    _provenanceMap: pMap,
  };
}

// ── Manual record helpers ─────────────────────────────────────────────────────

/**
 * Appends a new manually-entered record to a profile section and marks it
 * with manual provenance. Returns the updated FullSeafarerData.
 */
export function addManualRecord<T extends InternalRecordIdentity>(
  data: FullSeafarerData,
  section: ArraySection,
  item: T,
  keyFn: (i: T) => string,
): FullSeafarerData {
  const contentKey = keyFn(item);
  const existingPMap = data._provenanceMap ?? {};
  const usedIds = new Set(Object.keys(existingPMap));
  const stableId = uniqueRecordId('manual', usedIds);
  const pMap: ProvenanceMap = {
    ...existingPMap,
    [stableId]: { source: 'manual', recordId: stableId, contentKey: contentKey || stableId },
  };
  const existing = (data[section] as unknown as T[]) ?? [];
  return { ...data, [section]: [...existing, withRecordId(item, stableId)], _provenanceMap: pMap };
}

/**
 * Updates an existing record in-place and marks it user_corrected if it was
 * previously a dg_import (or legacy). manual records stay manual.
 *
 * The stable ID (pMap key) never changes — only the contentKey field is updated
 * when identity fields (e.g. document number) change. This ensures future DG
 * re-imports containing the original values do not create duplicates: the stable
 * ID equals the original DG content key, so it remains in processedKeys during
 * mergeSection even after the displayed content has changed.
 *
 * importKey is kept for backward compat with pre-Task-17 stored data.
 */
export function markRowEdited<T extends InternalRecordIdentity>(
  data: FullSeafarerData,
  section: ArraySection,
  index: number,
  updatedItem: T,
  keyFn: (i: T) => string,
): FullSeafarerData {
  const arr = [...((data[section] as unknown as T[]) ?? [])];
  const oldItem = arr[index];
  const oldContentKey = keyFn(oldItem);
  const newContentKey = keyFn(updatedItem);

  const pMap: ProvenanceMap = { ...(data._provenanceMap ?? {}) };

  const stableId = oldItem._recordId
    ?? Object.entries(pMap).find(([, prov]) => (prov.contentKey ?? prov.recordId) === oldContentKey)?.[0]
    ?? uniqueRecordId('legacy', new Set(Object.keys(pMap)));
  const existingProv = pMap[stableId];

  const newSource: ProvenanceSource =
    existingProv?.source === 'manual' ? 'manual' : 'user_corrected';

  // Retain importKey to trace original DG content key (backward compat).
  // For manual records importKey is never set (manual rows don't gate DG dedup).
  let importKey: string | undefined;
  if (newSource === 'user_corrected') {
    if (oldContentKey !== newContentKey) {
      importKey = existingProv?.importKey ?? oldContentKey;
    } else {
      importKey = existingProv?.importKey;
    }
  }

  // Update the existing entry in-place: contentKey changes, stable ID stays.
  pMap[stableId] = {
    source: newSource,
    recordId: stableId,
    contentKey: newContentKey,
    importedAt: existingProv?.importedAt,
    isHistorical: existingProv?.isHistorical,
    isLegacy: existingProv?.isLegacy,
    ...(importKey !== undefined && { importKey }),
  };

  arr[index] = withRecordId(updatedItem, stableId);
  return { ...data, [section]: arr, _provenanceMap: pMap };
}

// ── Historical record filter ──────────────────────────────────────────────────

/**
 * Returns a shallow copy of FullSeafarerData with all isHistorical=true records
 * removed from every array section. Used before building certificates so that
 * historical records do not generate active alerts.
 *
 * The original data object is not mutated.
 */
export function filterHistoricalRecords(data: FullSeafarerData): FullSeafarerData {
  const pMap = data._provenanceMap ?? {};
  const isActive = <T extends InternalRecordIdentity>(item: T, keyFn: (item: T) => string): boolean => {
    if (item._recordId) return pMap[item._recordId]?.isHistorical !== true;
    const contentKey = keyFn(item);
    const matches = Object.values(pMap).filter(p => (p.contentKey ?? p.recordId) === contentKey);
    return matches.length !== 1 || matches[0].isHistorical !== true;
  };
  return {
    ...data,
    modularCourses:             (data.modularCourses ?? []).filter(c => isActive(c, courseKey)),
    seaGoingService:            (data.seaGoingService ?? []).filter(s => isActive(s, serviceKey)),
    authorisedDocuments:        (data.authorisedDocuments ?? []).filter(d => isActive(d, docKey)),
    certificatesOfEndorsement:  (data.certificatesOfEndorsement ?? []).filter(e => isActive(e, coeKey)),
    certificatesOfCompetency:   (data.certificatesOfCompetency ?? []).filter(c => isActive(c, cocKey)),
    education:                  (data.education ?? []).filter(e => isActive(e, educationKey)),
    preSeaTraining:             (data.preSeaTraining ?? []).filter(p => isActive(p, preSeaKey)),
    postSeaTraining:            (data.postSeaTraining ?? []).filter(p => isActive(p, postSeaKey)),
  };
}

// ── Certificate store merge (DG rebuild path only) ────────────────────────────

function meaningful(value?: string): boolean {
  const valueNorm = norm(value);
  return valueNorm !== '' && !['n/a', 'na', 'null', 'unknown', '-'].includes(valueNorm);
}

function certificateFamily(cert: Certificate): string {
  return norm(cert.canonicalId ?? cert.canonicalName ?? cert.name);
}

function isDcEndorsement(cert: Certificate): boolean {
  return cert.category === 'Endorsement'
    && /dc\s+endorsement/i.test(`${cert.originalName ?? ''} ${cert.name}`);
}

function dcLevel(cert: Certificate): string {
  return norm(`${cert.originalName ?? ''} ${cert.name}`).match(/\b(support|operational|management)\b/)?.[1] ?? '';
}

function hasDcCommodity(cert: Certificate): boolean {
  return /\b(oil|chemical|gas|lpg|lng|liquefied)\b/i.test(`${cert.originalName ?? ''} ${cert.name}`);
}

type LegacyCertMatch = {
  matched?: Certificate;
  ambiguous: boolean;
};

/**
 * Finds a rebuilt certificate using real-world identity priority:
 * certificate number first, then type/family, authority, and issue date.
 * A changed renewal date is allowed only when the remaining match is unique.
 */
function findLegacyCertificateMatch(cert: Certificate, newBuilt: Certificate[]): LegacyCertMatch {
  if (!meaningful(cert.certNumber)) return { ambiguous: true };

  const sameNumber = newBuilt.filter(candidate =>
    meaningful(candidate.certNumber) && norm(candidate.certNumber) === norm(cert.certNumber),
  );
  if (sameNumber.length === 0) return { ambiguous: false };

  const family = certificateFamily(cert);
  const dc = isDcEndorsement(cert);
  const level = dcLevel(cert);
  const compatible = sameNumber.filter(candidate => {
    if (candidate.category !== cert.category) return false;
    if (meaningful(cert.issuingAuthority) && meaningful(candidate.issuingAuthority)
      && norm(candidate.issuingAuthority) !== norm(cert.issuingAuthority)) return false;

    if (dc && isDcEndorsement(candidate)) {
      const candidateLevel = dcLevel(candidate);
      return !level || !candidateLevel || level === candidateLevel;
    }
    return family !== '' && certificateFamily(candidate) === family;
  });

  const exactDate = compatible.filter(candidate =>
    meaningful(cert.issueDate) && meaningful(candidate.issueDate)
      && norm(candidate.issueDate) === norm(cert.issueDate),
  );
  if (exactDate.length === 1) return { matched: exactDate[0], ambiguous: false };
  if (exactDate.length > 1) return { ambiguous: true };
  if (compatible.length === 1) return { matched: compatible[0], ambiguous: false };
  return { ambiguous: compatible.length > 1 };
}

function legacyCertificateWasEdited(cert: Certificate, matched: Certificate): boolean {
  if (cert.source === 'manual' || cert.source === 'user_corrected' || cert.id.startsWith('manual-')) {
    return true;
  }
  const dcCommodityCorrection = isDcEndorsement(cert)
    && isDcEndorsement(matched)
    && hasDcCommodity(cert)
    && !hasDcCommodity(matched);
  const renewalCorrection = norm(cert.issueDate) !== norm(matched.issueDate)
    || norm(cert.expiryDate) !== norm(matched.expiryDate);
  return dcCommodityCorrection || renewalCorrection;
}

/**
 * Migrates preserved certificates onto rebuilt stable IDs before ID-based
 * deduplication. Older Renew/DC workflows sometimes left source absent or
 * dg_import, so sequential cert-N records are assessed by identity and edits,
 * not trusted source metadata alone.
 *
 * Unmatched or ambiguous sequential records are retained as user_corrected. A
 * duplicate is preferable to silently deleting a real renewal or commodity
 * classification.
 */
function migrateLegacyCertIds(
  existing: Certificate[],
  newBuilt: Certificate[],
): Certificate[] {
  const builtIds = new Set(newBuilt.map(c => c.id));

  return existing.map(cert => {
    if (builtIds.has(cert.id)) return cert;

    const sequential = /^cert-\d+$/.test(cert.id);
    const explicitlyPreserved = cert.source === 'manual'
      || cert.source === 'user_corrected'
      || cert.id.startsWith('manual-');
    if (!sequential && !explicitlyPreserved) return cert;

    const { matched } = findLegacyCertificateMatch(cert, newBuilt);
    if (matched && legacyCertificateWasEdited(cert, matched)) {
      return {
        ...cert,
        id: matched.id,
        source: cert.source === 'manual' ? 'manual' : 'user_corrected',
      };
    }
    if (matched) return cert;

    if (explicitlyPreserved || sequential) {
      return {
        ...cert,
        source: cert.source === 'manual' ? 'manual' : 'user_corrected',
      };
    }
    return cert;
  });
}

/**
 * Merges freshly DG-built certificates with the existing certificate store.
 *
 * This function is for the DG-rebuild / import path only. Direct user mutations
 * (Add Certificate, Renew, DC classify) should use saveCerts() in the context
 * to preserve the caller's source attribution exactly.
 *
 * Rules:
 * - manual / user_corrected certs from the existing store: always kept.
 *   Identified by source field or legacy id prefix "manual-".
 * - Legacy sequential cert-N renewals/DC corrections are migrated using
 *   number-first identity matching even when old source metadata is unreliable.
 * - Unmatched or ambiguous sequential records are preserved rather than
 *   silently removed.
 * - Dedup is id-only: a preserved cert blocks only the rebuilt DG cert with the
 *   same id. Two distinct certs in the same canonical family with different ids
 *   both survive (upstream deduplication in the cert builder handles within-DG
 *   family reduction).
 * - All new DG-built certs are tagged source=dg_import.
 */
export function mergeCertificates(
  existing: Certificate[],
  newBuilt: Certificate[],
): Certificate[] {
  const migrated = migrateLegacyCertIds(existing, newBuilt);

  const preserved = migrated.filter(
    c => c.source === 'manual' || c.source === 'user_corrected' || c.id.startsWith('manual-'),
  );
  const preservedIds = new Set(preserved.map(c => c.id));

  const dgCerts = newBuilt
    .filter(c => !preservedIds.has(c.id))
    .map(c => ({ ...c, source: 'dg_import' as const }));

  return [...preserved, ...dgCerts];
}
